'use client';

import { IconResolver, type IconType } from '../IconResolver';
import { Text } from '../Text';
import { tv } from '../_lib/utils';
import { ComponentButton, type ComponentButtonProps } from '../Inputs/Button';
import type { ReactNode } from 'react';

const styles = tv({
	slots: {
		icon: 'text-foreground-brand-primary flex-none w-5 h-5',
		wrapper: 'flex items-start',
		item: 'w-full flex flex-col',
		title: 'text-text-primary',
	},
	variants: {
		type: {
			icon: {},
			image: {
				icon: 'aspect-square [&_img]:h-full [&_img]:object-cover! max-w-12 lg:max-w-20',
			},
		},
		size: {
			small: {},
			medium: {
				wrapper: 'lg:border-b border-border-primary',
			},
		},
	},
	compoundVariants: [
		{
			type: 'icon',
			size: 'small',
			className: {
				wrapper: 'py-responsive-xs gap-responsive-2xs',
				item: 'gap-spacing-4xs',
			},
		},
		{
			type: 'icon',
			size: 'medium',
			className: {
				wrapper: 'py-responsive-xs gap-responsive-2xs lg:py-responsive-xl lg:gap-spacing-xs',
				item: 'gap-spacing-xs lg:gap-responsive-lg lg:flex-row',
				icon: 'mt-1.5',
				title: 'lg:flex-none lg:w-64',
			},
		},
		{
			type: 'image',
			size: 'small',
			className: {
				wrapper: 'py-responsive-xs gap-responsive-lg lg:gap-responsive-md',
				item: 'gap-spacing-4xs',
			},
		},
		{
			type: 'image',
			size: 'medium',
			className: {
				wrapper: 'py-responsive-xs gap-responsive-lg lg:py-responsive-xl lg:gap-responsive-lg',
				item: 'gap-spacing-xs lg:gap-spacing-3xs',
				icon: 'min-w-12 min-h-12 lg:min-w-32 lg:min-h-32',
			},
		},
	],
});

export type FeaturesListItemProps = {
	icon?: IconType;
	title?: ReactNode;
	description?: string;
	button?: ComponentButtonProps;
	size?: 'small' | 'medium';
};

export function FeaturesListItem(props: FeaturesListItemProps) {
	const type: 'icon' | 'image' = typeof props.icon === 'object' ? 'image' : 'icon';
	const size: 'small' | 'medium' = props.size ?? 'small';
	const { icon, wrapper, item, title } = styles({ type, size });

	return (
		<li className={wrapper()} data-component='FeaturesListItem'>
			<IconResolver className={icon()} icon={props.icon ?? 'dot-outline'} />

			<div className={item()}>
				{props.title && (
					<Text as='h3' styleType={size === 'small' ? 'heading-4xs' : 'body-2xl-medium'} className={title()}>
						{props.title}
					</Text>
				)}

				<div className='flex flex-col items-start gap-spacing-xs'>
					{props.description && (
						<Text styleType='body-md' className='text-text-secondary'>
							{props.description}
						</Text>
					)}

					{props.button && <ComponentButton {...props.button} />}
				</div>
			</div>
		</li>
	);
}
